/**
 * BettingControls — Action buttons, raise slider, presets, All-In confirmation
 *
 * Requirements: 18.1, 18.2, 18.3, 18.4, 18.5, 18.6, 18.7, 18.8, 23.5, 25.1
 */

import React, { useState, useCallback } from 'react';
import { BettingControlsProps, PlayerAction, ValidAction } from '../types';
import './BettingControls.css';

function findAction(actions: ValidAction[], type: string): ValidAction | undefined {
  return actions.find((a) => a.type === type);
}

export const BettingControls: React.FC<BettingControlsProps> = ({
  validActions,
  pot,
  callAmount,
  minRaise,
  maxRaise,
  playerStack,
  onAction,
  disabled,
}) => {
  const [raiseAmount, setRaiseAmount] = useState(minRaise);
  const [showAllInConfirm, setShowAllInConfirm] = useState(false);

  const canFold = validActions.some((a) => a.type === 'fold');
  const canCheck = validActions.some((a) => a.type === 'check');
  const canCall = validActions.some((a) => a.type === 'call');
  const canRaise = validActions.some((a) => a.type === 'raise');
  const canAllIn = validActions.some((a) => a.type === 'all-in');

  const raiseAction = findAction(validActions, 'raise');
  const effectiveMinRaise = raiseAction?.minAmount ?? minRaise;
  const effectiveMaxRaise = raiseAction?.maxAmount ?? maxRaise;

  // Sync slider when minRaise changes
  React.useEffect(() => {
    setRaiseAmount(effectiveMinRaise);
  }, [effectiveMinRaise]);

  const handleFold = useCallback(() => {
    onAction({ type: 'fold' });
  }, [onAction]);

  const handleCheck = useCallback(() => {
    onAction({ type: 'check' });
  }, [onAction]);

  const handleCall = useCallback(() => {
    onAction({ type: 'call' });
  }, [onAction]);

  const handleRaise = useCallback(() => {
    onAction({ type: 'raise', amount: raiseAmount });
  }, [onAction, raiseAmount]);

  const handleAllInRequest = useCallback(() => {
    setShowAllInConfirm(true);
  }, []);

  const handleAllInConfirm = useCallback(() => {
    setShowAllInConfirm(false);
    onAction({ type: 'all-in' });
  }, [onAction]);

  const handleAllInCancel = useCallback(() => {
    setShowAllInConfirm(false);
  }, []);

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRaiseAmount(Number(e.target.value));
    },
    []
  );

  const handlePreset = useCallback(
    (fraction: number) => {
      const amount = Math.round(pot * fraction);
      const clamped = Math.max(effectiveMinRaise, Math.min(effectiveMaxRaise, amount));
      setRaiseAmount(clamped);
    },
    [pot, effectiveMinRaise, effectiveMaxRaise]
  );

  const handlePresetAllIn = useCallback(() => {
    setRaiseAmount(effectiveMaxRaise);
  }, [effectiveMaxRaise]);

  const containerClass = `poker-betting-controls ${disabled ? 'poker-betting-controls--disabled' : ''}`;

  return (
    <div className={containerClass} role="toolbar" aria-label="Betting controls">
      {/* Action buttons */}
      <div className="poker-betting-actions">
        {canFold && (
          <button
            className="poker-action-btn poker-action-btn--fold"
            onClick={handleFold}
            disabled={disabled}
            aria-label="Fold"
          >
            Fold
          </button>
        )}

        {canCheck && (
          <button
            className="poker-action-btn poker-action-btn--check"
            onClick={handleCheck}
            disabled={disabled}
            aria-label="Check"
          >
            Check
          </button>
        )}

        {canCall && (
          <button
            className="poker-action-btn poker-action-btn--call"
            onClick={handleCall}
            disabled={disabled}
            aria-label={`Call ${callAmount}`}
          >
            Call {callAmount}
          </button>
        )}

        {canRaise && (
          <button
            className="poker-action-btn poker-action-btn--raise"
            onClick={handleRaise}
            disabled={disabled}
            aria-label={`Raise to ${raiseAmount}`}
          >
            Raise {raiseAmount}
          </button>
        )}

        {canAllIn && (
          <button
            className="poker-action-btn poker-action-btn--all-in"
            onClick={handleAllInRequest}
            disabled={disabled}
            aria-label={`All-In ${playerStack}`}
          >
            All-In
          </button>
        )}
      </div>

      {/* Raise slider and presets */}
      {canRaise && (
        <div className="poker-raise-section">
          <div className="poker-raise-slider-row">
            <input
              type="range"
              className="poker-raise-slider"
              min={effectiveMinRaise}
              max={effectiveMaxRaise}
              value={raiseAmount}
              onChange={handleSliderChange}
              disabled={disabled}
              aria-label="Raise amount"
              aria-valuemin={effectiveMinRaise}
              aria-valuemax={effectiveMaxRaise}
              aria-valuenow={raiseAmount}
            />
            <span className="poker-raise-amount">{raiseAmount}</span>
          </div>

          <div className="poker-raise-presets">
            <button
              className="poker-preset-btn"
              onClick={() => handlePreset(0.5)}
              disabled={disabled}
              aria-label="Raise half pot"
            >
              ½ Pot
            </button>
            <button
              className="poker-preset-btn"
              onClick={() => handlePreset(0.75)}
              disabled={disabled}
              aria-label="Raise three-quarter pot"
            >
              ¾ Pot
            </button>
            <button
              className="poker-preset-btn"
              onClick={() => handlePreset(1)}
              disabled={disabled}
              aria-label="Raise full pot"
            >
              Pot
            </button>
            <button
              className="poker-preset-btn"
              onClick={handlePresetAllIn}
              disabled={disabled}
              aria-label="Set raise to all-in"
            >
              All-In
            </button>
          </div>
        </div>
      )}

      {/* All-In confirmation dialog */}
      {showAllInConfirm && (
        <div
          className="poker-allin-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="All-In confirmation"
        >
          <div className="poker-allin-dialog">
            <h3 className="poker-allin-dialog__title">Go All-In?</h3>
            <p className="poker-allin-dialog__message">
              You are about to bet all your remaining chips.
            </p>
            <div className="poker-allin-dialog__amount">{playerStack} chips</div>
            <div className="poker-allin-dialog__actions">
              <button
                className="poker-allin-dialog__btn poker-allin-dialog__btn--cancel"
                onClick={handleAllInCancel}
                aria-label="Cancel all-in"
              >
                Cancel
              </button>
              <button
                className="poker-allin-dialog__btn poker-allin-dialog__btn--confirm"
                onClick={handleAllInConfirm}
                aria-label="Confirm all-in"
                autoFocus
              >
                Confirm All-In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BettingControls;
